<script lang='ts' context='module'>

    /* IMPORTS --------------------------------------------------------------------------------- */

    // CASSIOPEIA
    import {
        AUCTION_API,
        BrandSelectMultiple,
        Button,
        Card,
        Heading,
        QUERY_STATE,
        SelectCategories,
        SelectChannels,
        SelectMarkets,
        SelectVertical,
        SubscriptionList,
        ToggleAuctionMatches,
        VERTICAL_DEFAULT,
    } from '@smatch/cassiopeia-web-components';

    /* TYPES ------------------------------------------------------------------------------------ */

    type QueryParameters = Required<Pick<AUCTION_API.QueryParameters, 'brandKeys'
        | 'categoryKeys'
        | 'channelCodes'
        | 'marketCodes'
        | 'showOnlyMatches'
        | 'states'
        | 'verticalCode'
    >>;

    /* CONFIG ----------------------------------------------------------------------------------- */

    const STATES = [
        'created',
        'pending',
        'approved',
        'open',
    ];

    /* INTERFACES ------------------------------------------------------------------------------- */

    export const DEFAULTS: QueryParameters = {
        brandKeys: [],
        categoryKeys: [],
        channelCodes: [],
        marketCodes: [],
        showOnlyMatches: false,
        states: STATES,
        verticalCode: VERTICAL_DEFAULT,
    };

</script>

<script lang='ts'>

    /* IMPORTS --------------------------------------------------------------------------------- */

    // STYLING
    import 'tailwindcss/tailwind.css';

    // PLUGINS
    import { onDestroy, onMount, getContext } from 'svelte';

    // TYPES
    import type { Unsubscriber } from 'svelte/store';
    import type { Parameters } from 'src/app/types';

    /* INITIALIZE ------------------------------------------------------------------------------ */

    const SCOPE: string = getContext('scope');

    const getDefault = (): QueryParameters => {
        const PARAMETERS = QUERY_STATE.getScope(SCOPE) as QueryParameters | null;

        if (PARAMETERS === null) {
            return { ...DEFAULTS };
        }

        return Object.fromEntries(Object
            .entries(DEFAULTS)
            .map(([ key, value ]) => [ key, PARAMETERS[key] ?? value ])) as QueryParameters;
    };

    let parameters: QueryParameters = getDefault();
    let filterParameters: AUCTION_API.QueryParameters | null = null;
    let unsubscribeFilters: Unsubscriber;
    let unsubscribeParameters: Unsubscriber;

    const SUBSCRIPTION_PARAMETERS = { states: [ 'created', 'paused' ] };

    /* INTERNALS ------------------------------------------------------------------------------- */

    $: QUERY_STATE.updateScope({ ...parameters }, SCOPE);

    const reset = (): void => {
        parameters = { ...DEFAULTS };
    };

    onMount(() => {
        unsubscribeFilters = QUERY_STATE.subscribeScope(SCOPE, (data: Parameters | null): void => {
            filterParameters = { ...data };
        });

        unsubscribeParameters = QUERY_STATE.subscribeScope(SCOPE, (): void => {
            parameters = getDefault();
        });
    });

    onDestroy(() => {
        unsubscribeFilters();
        unsubscribeParameters();
    });

</script>

<div id = 's8-filter-auctions'>

    <div class = 's8-header'>
        <Heading size = 'md'>Filter</Heading>
        <Button
            label = 'Reset'
            color = 'neutral'
            style = 'ghost'
            icon = 'reset'
            align = 'left'
            size = 'sm'
            on:click = { reset }
        />
    </div>

    <div class = 's8-body'>

        {#if filterParameters !== null}

            {#await AUCTION_API.listAuctionFilters(filterParameters) then filters }

                {#if filters.auction?.verticalCodes !== undefined && filters.auction.verticalCodes.length > 1}

                    <SelectVertical
                        bind:key = { parameters.verticalCode }
                        whitelist = { filters.auction.verticalCodes }
                        label = { null }
                    />

                {/if}

                {#if filters.product !== undefined}

                    <BrandSelectMultiple
                        bind:keys = { parameters.brandKeys }
                        whitelist = { filters.product.brandKeys }
                        placeholder = 'Brands'
                        label = { null }
                    />

                    <SelectCategories
                        bind:keys = { parameters.categoryKeys }
                        verticalCode = { parameters.verticalCode }
                        whitelist = { filters.product.categoryKeys }
                        placeholder = 'Categories'
                        label = { null }
                    />

                {/if}

                {#if filters.auction !== undefined}

                    <SelectMarkets
                        bind:keys = { parameters.marketCodes }
                        placeholder = 'Regions'
                        label = 'Approved for Distribution in:'
                        whitelist = { filters.auction.marketCodes }
                    />

                    <SelectChannels
                        bind:keys = { parameters.channelCodes }
                        minItems = { 0 }
                        label = 'Approved Distribution Channels:'
                        whitelist = { filters.auction.channelCodes }
                    />

                {/if}

                {#if filters.auction?.hasMatches}

                    <SubscriptionList parameters = { SUBSCRIPTION_PARAMETERS }>
                        <Card size = 'md' css = '!mb-8'>
                            <ToggleAuctionMatches bind:isChecked = { parameters.showOnlyMatches } />
                        </Card>
                    </SubscriptionList>

                {/if}

            {/await}
        {/if}
    </div>

</div>

<style lang='postcss'>

    div#s8-filter-auctions {
        div.s8-header {
            display: flex;
            padding: 5px 0px 12px 0px;

            :global(> *:first-child) {
                flex-grow: 1;
            }
        }
    }

</style>